import { TranslationsProvider } from '@datacamp/le-shared-components';
import { ToastProvider } from '@datacamp/waffles/toast';
import cn from 'classnames';
import raven from 'raven-js';
import React, { Component } from 'react';

import config from '../config';
import ExerciseFooter from '../containers/ExerciseFooter';
import ModalHandler from '../containers/ModalHandler';
import ScreenHandler from '../containers/ScreenHandler';
import { showLearnRecap } from '../helpers/learnRecap';
import type { LearnRecapState } from '../redux/reducers/learnRecap';
import { PreFetchedDataState } from '../redux/reducers/preFetchedData';
import { BOOT_ERRORS } from '../redux/selectors';

import ContentAuthorizationService from './ContentAuthorizationService/ContentAuthorizationService';
import ErrorPage from './ErrorPage';
import ExerciseHandler from './ExerciseHandler';
import Header from './Header';
import HeadManager from './HeadManager';
import OnboardingMilestones from './OnboardingMilestones';
import SearchLandingPopUp from './SearchLandingPopup';
import ToastOnUserStatusError from './ToastOnUserStatusError';

type Props = {
  bootError?: string;
  courseId?: number;
  headProps?: any;
  isApplicationBootable: boolean;
  isPreBooted: boolean;
  isUserEnabledForLearnRecap: boolean;
  language: string;
  learnRecap: LearnRecapState;
  onBoot: (...args: any[]) => any;
  preFetchedData: PreFetchedDataState;
  progressIndicatorVisible: boolean;
  showLearnRecapModal: () => void;
  showShowOrSkipRecapModal: () => void;
  uiTheme: 'LIGHT' | 'DARK'; // TODO: PropTypes.shape(HeadManager.propTypes)
  userId?: number;
};

class App extends Component<Props> {
  private userEligibleForLearnRecap({
    courseId,
    userId,
  }: Partial<{ courseId: number; userId: number }>): void {
    if (window.dataLayer && courseId && userId) {
      window.dataLayer.push({
        event: 'heap_event',
        heap_event_name: 'Learn - Campus - User Eligible For Learn Recap',
        heap_event_properties: {
          app_id: config.appName,
          event_category: 'learning',
          identity: userId,
          course_id: courseId,
        },
      });
    }
  }

  componentWillMount(): void {
    const { isApplicationBootable, language, preFetchedData } = this.props;
    this.props.onBoot({ preFetchedData, isApplicationBootable, language });
  }

  componentDidCatch(error: any): void {
    raven.captureException(error);
  }

  componentDidUpdate(): void {
    if (this.props.isPreBooted && this.props.bootError === null) {
      showLearnRecap({
        courseId: this.props.courseId,
        isUserEnabledForLearnRecap: this.props.isUserEnabledForLearnRecap,
        learnRecap: this.props.learnRecap,
        showLearnRecapModal: this.props.showLearnRecapModal,
        showShowOrSkipRecapModal: this.props.showShowOrSkipRecapModal,
        userEligibleForLearnRecapCallback: () => {
          this.userEligibleForLearnRecap({
            courseId: this.props.courseId,
            userId: this.props.userId,
          });
        },
      });
    }
  }

  render() {
    const {
      bootError,
      headProps,
      isPreBooted,
      progressIndicatorVisible,
      uiTheme,
    } = this.props;
    const wrapperClasses = cn('theme', {
      'progress-indicator--visible': progressIndicatorVisible,
      'theme--light': uiTheme === 'LIGHT',
    });

    let errorProps = {};
    if (bootError === BOOT_ERRORS.LTI_NOT_ACTIVE) {
      errorProps = {
        pageTitle: 'Something went wrong with your edX session',
        title: 'Oops, something went wrong...',
        description:
          ' It looks like something went wrong with your edX session. Please close this page and try again.',
      };
    }

    return (
      <ToastProvider>
        <div className={wrapperClasses}>
          {bootError === null && <HeadManager {...headProps} />}
          {isPreBooted && bootError !== null && <ErrorPage {...errorProps} />}
          {isPreBooted && bootError === null && (
            <TranslationsProvider language={this.props.language}>
              {!config.isServerSideRendering ? (
                <ContentAuthorizationService />
              ) : null}
              <Header />
              <ExerciseHandler />
              {progressIndicatorVisible && <ExerciseFooter />}
              <SearchLandingPopUp />
              <ScreenHandler />
              <ModalHandler />
              <OnboardingMilestones />
              <ToastOnUserStatusError />
            </TranslationsProvider>
          )}
        </div>
      </ToastProvider>
    );
  }
}

export default App;
